/**
 * The synthesiser, and the starter sounds PadLab makes for itself.
 *
 * A soundboard that opens empty is a soundboard nobody plays, so the PWA generates its own
 * kit on first run rather than downloading one. Everything in `PERCUSSION_KINDS` is
 * synthesised here from oscillators, envelopes and a filter — no samples ship with the app,
 * which is why the starter content is CC0: there is nothing in it that anyone else owns.
 *
 * Patches are rendered to a buffer and then played, rather than synthesised live. That
 * trade buys three things the app needs more than it needs a live synth: a preview that is
 * bit-identical to the bounce, a render that can spend as much CPU per sample as it likes,
 * and one code path instead of a realtime one and an offline one that drift apart. It is
 * the same decision `SynthRenderer.swift` makes on iOS, with the same patch parameters, so
 * a kit generated on the phone and a kit generated in the browser are the same kit.
 */

import {
  ccZeroLicence,
  clamp,
  clampFinite,
  effectChainActiveNodes,
  makeEffectChain,
  makeEffectNode,
  quantizeBeats,
  type EffectChain,
  type Quantize,
  type SampleLicence,
} from '../model/types';
import {
  Biquad,
  RenderBudget,
  SeededRandom,
  applyEffectChain,
  normalizeChannels,
  panGains,
  type BiquadKind,
  type RenderedAudio,
} from './offline-render';

// MARK: - Patch model

export type SynthWaveform = 'sine' | 'triangle' | 'square' | 'saw' | 'noise';
export type SynthFilterType = 'off' | 'lowPass' | 'highPass' | 'bandPass' | 'notch';
export type SynthLFOTarget = 'pitch' | 'filter' | 'amplitude' | 'pan';

export const SYNTH_WAVEFORMS: readonly SynthWaveform[] = ['sine', 'triangle', 'square', 'saw', 'noise'];
export const SYNTH_FILTER_TYPES: readonly SynthFilterType[] = [
  'off',
  'lowPass',
  'highPass',
  'bandPass',
  'notch',
];
export const SYNTH_LFO_TARGETS: readonly SynthLFOTarget[] = ['pitch', 'filter', 'amplitude', 'pan'];

// MARK: - Seeds

/**
 * A patch seed, held as two 32-bit halves.
 *
 * The native renderer seeds every random decision from a `UInt64`, and the library derives
 * those seeds by multiplying a 64-bit hash — so a JS `number`, which is exact only to 53
 * bits, cannot carry one. Splitting it is what lets the browser reproduce the phone's noise
 * sample for sample instead of merely producing noise of the same colour.
 */
export interface SynthSeed {
  hi: number;
  lo: number;
}

export type SynthSeedLike = number | bigint | SynthSeed;

const UINT64_MASK = 0xffff_ffff_ffff_ffffn;

function toBigInt(seed: SynthSeed): bigint {
  return ((BigInt(seed.hi >>> 0) << 32n) | BigInt(seed.lo >>> 0)) & UINT64_MASK;
}

function fromBigInt(value: bigint): SynthSeed {
  const wrapped = value & UINT64_MASK;
  return { hi: Number(wrapped >> 32n), lo: Number(wrapped & 0xffff_ffffn) };
}

export function makeSynthSeed(value: SynthSeedLike): SynthSeed {
  if (typeof value === 'bigint') return fromBigInt(value);
  if (typeof value === 'number') {
    // Negative and fractional values arrive from hand-written presets and from hashes that
    // were not masked; wrapping them the way Swift's `UInt64(truncatingIfNeeded:)` does
    // keeps a seed a seed instead of turning it into NaN halves.
    return fromBigInt(BigInt(Math.trunc(Number.isFinite(value) ? value : 1)));
  }
  return { hi: value.hi >>> 0, lo: value.lo >>> 0 };
}

/** SplitMix64's odd multiplier, as used by the native renderer to fan one seed out per voice. */
const VOICE_SEED_MULTIPLIER = 6364136223846793005n;

/** The constant the native renderer offsets the LFO's sample-and-hold stream by. */
const LFO_SEED_OFFSET = 0x5dee_ce66_d000_0001n;

function voiceSeed(seed: SynthSeed, layerIndex: number, voiceIndex: number): SynthSeed {
  const base = toBigInt(seed) * VOICE_SEED_MULTIPLIER;
  return fromBigInt(base + BigInt(layerIndex * 61 + voiceIndex * 7) + 1n);
}

function lfoSeed(seed: SynthSeed): SynthSeed {
  return fromBigInt(toBigInt(seed) + LFO_SEED_OFFSET);
}

export function synthWaveformDisplayName(waveform: SynthWaveform): string {
  switch (waveform) {
    case 'sine':
      return 'Sine';
    case 'triangle':
      return 'Triangle';
    case 'square':
      return 'Square';
    case 'saw':
      return 'Saw';
    case 'noise':
      return 'Noise';
  }
}

/**
 * A DAHDSR with a curve control and an optional burst repeat on the front.
 *
 * Deliberately *not* `AmpEnvelope`: that one describes how a recorded sample is re-shaped
 * on playback and has to stay small because every pad stores one. A patch envelope is a
 * different job and can afford the extra stages.
 */
export interface SynthEnvelope {
  /** Silence before the envelope starts. */
  delay: number;
  attack: number;
  /** Time held at full level after the attack, before the decay begins. */
  hold: number;
  decay: number;
  /** Level the decay falls to, held until the note is released. */
  sustain: number;
  release: number;
  /**
   * Bends attack, decay and release together. 0 is a straight line, positive moves fast
   * then slows (percussive), negative eases in then rushes (a swell). -1…+1.
   */
  curve: number;
  /**
   * How many times the attack + decay front is retriggered before the tail. A hand clap is
   * several hands hitting a few milliseconds apart, and this is the only part of the shape
   * that makes it read as a clap rather than as one noise burst.
   */
  burstCount: number;
  burstInterval: number;
}

export function makeSynthEnvelope(options: Partial<SynthEnvelope> = {}): SynthEnvelope {
  return {
    delay: options.delay ?? 0,
    attack: options.attack ?? 0.002,
    hold: options.hold ?? 0,
    decay: options.decay ?? 0.25,
    sustain: options.sustain ?? 0,
    release: options.release ?? 0.05,
    curve: options.curve ?? 0.6,
    burstCount: options.burstCount ?? 1,
    burstInterval: options.burstInterval ?? 0.008,
  };
}

function sanitiseEnvelope(envelope: SynthEnvelope): SynthEnvelope {
  return {
    delay: clampFinite(envelope.delay, 0, 5, 0),
    // An attack of literally zero starts the oscillator at full scale, which is a click on
    // every render. Ten samples at 48 kHz is inaudible and cannot click.
    attack: clampFinite(envelope.attack, 0.0002, 20, 0.002),
    hold: clampFinite(envelope.hold, 0, 5, 0),
    decay: clampFinite(envelope.decay, 0, 30, 0.25),
    sustain: clampFinite(envelope.sustain, 0, 1, 0),
    release: clampFinite(envelope.release, 0.001, 30, 0.05),
    curve: clampFinite(envelope.curve, -1, 1, 0.6),
    burstCount: Math.round(clampFinite(envelope.burstCount, 1, 8, 1)),
    burstInterval: clampFinite(envelope.burstInterval, 0.001, 0.2, 0.008),
  };
}

/** 1 is a straight line; larger numbers bend the segment towards its percussive shape. */
function curveExponent(curve: number): number {
  return Math.pow(3, clamp(curve, -1, 1));
}

function rise(position: number, exponent: number): number {
  const p = clamp(position, 0, 1);
  return exponent === 1 ? p : Math.pow(p, 1 / exponent);
}

function fall(position: number, exponent: number): number {
  const p = clamp(position, 0, 1);
  return exponent === 1 ? 1 - p : Math.pow(1 - p, exponent);
}

function singleShot(envelope: SynthEnvelope, time: number, gateTime: number, exponent: number): number {
  if (time < envelope.delay) return 0;
  const local = time - envelope.delay;

  if (local < envelope.attack) return envelope.attack > 0 ? rise(local / envelope.attack, exponent) : 1;

  let elapsed = local - envelope.attack;
  if (elapsed < envelope.hold) return 1;
  elapsed -= envelope.hold;

  // Everything below is measured from the end of the hold stage, so the gate has to be
  // rebased the same way.
  const sustainWindow = Math.max(0, gateTime - envelope.delay - envelope.attack - envelope.hold);

  if (elapsed < sustainWindow) {
    if (envelope.decay <= 0) return envelope.sustain;
    if (elapsed < envelope.decay) {
      return envelope.sustain + (1 - envelope.sustain) * fall(elapsed / envelope.decay, exponent);
    }
    return envelope.sustain;
  }

  if (envelope.release <= 0) return 0;
  const levelAtGate =
    envelope.decay > 0 && sustainWindow < envelope.decay
      ? envelope.sustain + (1 - envelope.sustain) * fall(sustainWindow / envelope.decay, exponent)
      : envelope.sustain;
  const progress = (elapsed - sustainWindow) / envelope.release;
  if (progress >= 1) return 0;
  return levelAtGate * fall(progress, exponent);
}

/**
 * Level at `time`, for a note released at `gateTime`. Bursts are combined with `max` rather
 * than by summing, so three overlapping copies still peak at full scale instead of clipping.
 */
export function envelopeValue(envelope: SynthEnvelope, time: number, gateTime: number): number {
  if (time < 0) return 0;
  const exponent = curveExponent(envelope.curve);
  let level = singleShot(envelope, time, gateTime, exponent);
  if (envelope.burstCount <= 1 || envelope.burstInterval <= 0) return level;
  for (let index = 1; index < envelope.burstCount; index++) {
    const onset = index * envelope.burstInterval;
    if (time < onset) break;
    level = Math.max(level, singleShot(envelope, time - onset, gateTime, exponent));
  }
  return level;
}

export interface SynthFilter {
  type: SynthFilterType;
  /** Cutoff, or centre for band-pass and notch, in hertz before key tracking. */
  cutoff: number;
  /** Q. Above about 4 the filter starts to ring, which is where metallic tones live. */
  resonance: number;
  /** How far the filter envelope moves the cutoff, in octaves. Negative closes it. */
  envelopeAmount: number;
  envelope: SynthEnvelope;
  /** How much the cutoff follows the base frequency, 0…1. */
  keyTracking: number;
}

export function makeSynthFilter(options: Partial<SynthFilter> = {}): SynthFilter {
  return {
    type: options.type ?? 'off',
    cutoff: options.cutoff ?? 12000,
    resonance: options.resonance ?? 0.707,
    envelopeAmount: options.envelopeAmount ?? 0,
    envelope: options.envelope ?? makeSynthEnvelope({ attack: 0.001, decay: 0.2, sustain: 0, release: 0.05 }),
    keyTracking: options.keyTracking ?? 0,
  };
}

/** Middle C is the reference, so a patch left at the default base frequency is unaffected. */
function trackedCutoff(filter: SynthFilter, baseFrequency: number): number {
  if (filter.keyTracking <= 0) return filter.cutoff;
  return filter.cutoff * Math.pow(baseFrequency / 261.6255653, filter.keyTracking);
}

function sanitiseFilter(filter: SynthFilter): SynthFilter {
  return {
    type: filter.type,
    cutoff: clampFinite(filter.cutoff, 20, 20000, 12000),
    // Below 0.3 a biquad starts to lose gain rather than widen; above 20 it rings for
    // longer than any of these one-shots last.
    resonance: clampFinite(filter.resonance, 0.3, 20, 0.707),
    envelopeAmount: clampFinite(filter.envelopeAmount, -8, 8, 0),
    envelope: sanitiseEnvelope(filter.envelope),
    keyTracking: clampFinite(filter.keyTracking, 0, 1, 0),
  };
}

export interface SynthPitchEnvelope {
  /** Offset at the very start, in semitones. Positive sweeps down to the base frequency. */
  amountSemitones: number;
  decay: number;
  /** Positive drops fast then settles; negative glides. -1…+1. */
  curve: number;
}

export function makeSynthPitchEnvelope(options: Partial<SynthPitchEnvelope> = {}): SynthPitchEnvelope {
  return {
    amountSemitones: options.amountSemitones ?? 0,
    decay: options.decay ?? 0.05,
    curve: options.curve ?? 0.7,
  };
}

export function pitchEnvelopeValue(envelope: SynthPitchEnvelope, time: number): number {
  if (envelope.amountSemitones === 0 || envelope.decay <= 0 || time < 0 || time >= envelope.decay) return 0;
  return envelope.amountSemitones * Math.pow(1 - time / envelope.decay, curveExponent(envelope.curve));
}

export interface SynthLFO {
  isEnabled: boolean;
  waveform: SynthWaveform;
  /** Free-running rate in hertz, used when `isTempoSynced` is false. */
  rateHz: number;
  /**
   * 0…1. What it means depends on the target: a full-depth pitch LFO is ±1 octave, a
   * full-depth filter LFO is ±4 octaves of cutoff.
   */
  depth: number;
  target: SynthLFOTarget;
  isTempoSynced: boolean;
  /** Grid the synced rate locks to. `off` falls back to `rateHz`. */
  syncDivision: Quantize;
  /**
   * Starting point in the cycle, 0…1. Fixed rather than free-running, so two renders of the
   * same patch are identical.
   */
  phase: number;
}

export function makeSynthLFO(options: Partial<SynthLFO> = {}): SynthLFO {
  return {
    isEnabled: options.isEnabled ?? false,
    waveform: options.waveform ?? 'sine',
    rateHz: options.rateHz ?? 5,
    depth: options.depth ?? 0,
    target: options.target ?? 'pitch',
    isTempoSynced: options.isTempoSynced ?? false,
    syncDivision: options.syncDivision ?? 'eighth',
    phase: options.phase ?? 0,
  };
}

function sanitiseLFO(lfo: SynthLFO): SynthLFO {
  const phase = clampFinite(lfo.phase, -1e6, 1e6, 0);
  return {
    ...lfo,
    rateHz: clampFinite(lfo.rateHz, 0.01, 200, 5),
    depth: clampFinite(lfo.depth, 0, 1, 0),
    phase: phase - Math.floor(phase),
  };
}

/**
 * Rate in hertz, resolving tempo sync against the patch's reference tempo. Zero means the
 * modulator is doing nothing and the renderer can skip it entirely.
 */
export function lfoEffectiveRate(lfo: SynthLFO, tempo: number): number {
  if (!lfo.isEnabled || lfo.depth <= 0) return 0;
  if (lfo.isTempoSynced) {
    const beats = quantizeBeats(lfo.syncDivision);
    if (beats !== null && beats > 0) return Math.max(tempo, 20) / 60 / beats;
  }
  return lfo.rateHz;
}

export interface SynthLayer {
  waveform: SynthWaveform;
  semitones: number;
  cents: number;
  level: number;
  pan: number;
  unisonVoices: number;
  unisonDetuneCents: number;
  /**
   * True starts every render at `startPhase`, which is what makes a kick's first cycle
   * identical every time. False takes the starting phase from the patch seed instead, so
   * stacked layers do not all cancel in the same place — still reproducible, just not
   * aligned.
   */
  resetsPhase: boolean;
  startPhase: number;
  isEnabled: boolean;
}

/** Both limits match `SynthLayer.maxUnisonVoices` and `SynthPatch.maxLayers` on iOS. */
export const MAX_UNISON_VOICES = 5;
export const MAX_LAYERS = 3;

export function makeSynthLayer(options: Partial<SynthLayer> = {}): SynthLayer {
  return {
    waveform: options.waveform ?? 'sine',
    semitones: options.semitones ?? 0,
    cents: options.cents ?? 0,
    level: options.level ?? 1,
    pan: options.pan ?? 0,
    unisonVoices: Math.round(clampFinite(options.unisonVoices ?? 1, 1, MAX_UNISON_VOICES, 1)),
    unisonDetuneCents: options.unisonDetuneCents ?? 0,
    resetsPhase: options.resetsPhase ?? true,
    startPhase: options.startPhase ?? 0,
    isEnabled: options.isEnabled ?? true,
  };
}

export interface SynthPatch {
  name: string;
  /** SF Symbol name, shared with the native build; the icon layer maps it to an SVG. */
  symbolName: string;
  layers: SynthLayer[];
  amplitude: SynthEnvelope;
  filter: SynthFilter;
  pitchEnvelope: SynthPitchEnvelope;
  lfo: SynthLFO;
  baseFrequency: number;
  duration: number;
  /** Input saturation, 0…1. Sits between the oscillators and the filter. */
  drive: number;
  chain: EffectChain;
  /** Reference tempo for the tempo-synced LFO and for any tempo-aware effect in `chain`. */
  tempo: number;
  /**
   * Seeds every random decision in the render — noise, and the starting phase of any layer
   * that does not reset. Same seed, same audio, every time.
   */
  seed: SynthSeed;
}

export interface MakeSynthPatchOptions extends Partial<Omit<SynthPatch, 'seed'>> {
  seed?: SynthSeedLike;
}

export function makeSynthPatch(options: MakeSynthPatchOptions = {}): SynthPatch {
  return {
    name: options.name ?? 'Patch',
    symbolName: options.symbolName ?? 'waveform',
    layers: options.layers ?? [makeSynthLayer()],
    amplitude: options.amplitude ?? makeSynthEnvelope(),
    filter: options.filter ?? makeSynthFilter(),
    pitchEnvelope: options.pitchEnvelope ?? makeSynthPitchEnvelope(),
    lfo: options.lfo ?? makeSynthLFO(),
    baseFrequency: options.baseFrequency ?? 220,
    duration: options.duration ?? 1,
    drive: options.drive ?? 0,
    chain: options.chain ?? makeEffectChain(),
    tempo: options.tempo ?? 120,
    seed: makeSynthSeed(options.seed ?? 1),
  };
}

/**
 * Clamps everything into a range the renderer can trust, exactly as `SynthPatch.sanitise()`
 * does on iOS. Anything arriving from disk, from a randomiser, or from a hand-written recipe
 * goes through here first, so the two platforms never disagree about what an out-of-range
 * value means.
 */
function sanitisePatch(patch: SynthPatch): SynthPatch {
  const layers = patch.layers.slice(0, MAX_LAYERS).map((layer) => {
    const startPhase = clampFinite(layer.startPhase, -1e6, 1e6, 0);
    return {
      ...layer,
      level: clampFinite(layer.level, 0, 1, 1),
      semitones: clampFinite(layer.semitones, -48, 48, 0),
      cents: clampFinite(layer.cents, -100, 100, 0),
      pan: clampFinite(layer.pan, -1, 1, 0),
      unisonVoices: Math.round(clampFinite(layer.unisonVoices, 1, MAX_UNISON_VOICES, 1)),
      unisonDetuneCents: clampFinite(layer.unisonDetuneCents, 0, 100, 0),
      startPhase: startPhase - Math.floor(startPhase),
    };
  });
  const seed = makeSynthSeed(patch.seed);
  return {
    ...patch,
    layers: layers.length > 0 ? layers : [makeSynthLayer()],
    amplitude: sanitiseEnvelope(patch.amplitude),
    filter: sanitiseFilter(patch.filter),
    pitchEnvelope: {
      amountSemitones: clampFinite(patch.pitchEnvelope.amountSemitones, -72, 72, 0),
      decay: clampFinite(patch.pitchEnvelope.decay, 0, 10, 0.05),
      curve: clampFinite(patch.pitchEnvelope.curve, -1, 1, 0.7),
    },
    lfo: sanitiseLFO(patch.lfo),
    baseFrequency: clampFinite(patch.baseFrequency, 8, 20000, 220),
    duration: clampFinite(patch.duration, 0.02, 30, 1),
    drive: clampFinite(patch.drive, 0, 1, 0),
    tempo: clampFinite(patch.tempo, 20, 300, 120),
    // A zero seed would leave SplitMix64 stuck on its fixed point, which is silence for a
    // noise layer.
    seed: seed.hi === 0 && seed.lo === 0 ? { hi: 0, lo: 1 } : seed,
  };
}

/** The note is released this far before the end, so the release tail fits in the render. */
function gateTimeOf(patch: SynthPatch): number {
  return Math.max(0.0005, patch.duration - patch.amplitude.release);
}

// MARK: - Oscillators

/**
 * PolyBLEP correction around a discontinuity. Without it a square or saw aliases into a
 * chorus of inharmonic tones the moment it goes above a few hundred hertz, which is
 * exactly where a cowbell and a laser live.
 */
function polyBLEP(phase: number, increment: number): number {
  if (increment <= 0) return 0;
  if (phase < increment) {
    const t = phase / increment;
    return t + t - t * t - 1;
  }
  if (phase > 1 - increment) {
    const t = (phase - 1) / increment;
    return t * t + t + t + 1;
  }
  return 0;
}

interface Voice {
  waveform: SynthWaveform;
  phase: number;
  frequencyRatio: number;
  gain: number;
  pan: number;
  leftGain: number;
  rightGain: number;
  random: SeededRandom;
}

function makeVoices(patch: SynthPatch): Voice[] {
  const voices: Voice[] = [];
  for (let layerIndex = 0; layerIndex < patch.layers.length; layerIndex++) {
    const layer = patch.layers[layerIndex]!;
    if (!layer.isEnabled || layer.level <= 0) continue;

    const count = layer.unisonVoices;
    // Equal power rather than equal gain, so raising the unison count fattens the layer
    // instead of just making it louder.
    const voiceGain = layer.level / Math.sqrt(count);

    for (let voiceIndex = 0; voiceIndex < count; voiceIndex++) {
      const pair = voiceSeed(patch.seed, layerIndex, voiceIndex);
      const random = new SeededRandom(pair.hi, pair.lo);
      // -1 for the lowest copy, +1 for the highest, 0 when there is only one.
      const spread = count > 1 ? (voiceIndex / (count - 1)) * 2 - 1 : 0;
      const cents = layer.cents + spread * layer.unisonDetuneCents * 0.5;
      const ratio = Math.pow(2, (layer.semitones + cents / 100) / 12);
      // Detune only reads as width when the copies are also spread across the image;
      // stacked in the centre it just sounds out of tune.
      const pan = count > 1 ? clamp(layer.pan + spread * 0.35, -1, 1) : layer.pan;
      const gains = panGains(pan);
      voices.push({
        waveform: layer.waveform,
        phase: layer.resetsPhase ? layer.startPhase : random.unit(),
        frequencyRatio: ratio,
        gain: voiceGain,
        pan,
        leftGain: gains.left,
        rightGain: gains.right,
        random,
      });
    }
  }
  return voices;
}

function oscillator(voice: Voice, increment: number): number {
  switch (voice.waveform) {
    case 'sine':
      return Math.sin(2 * Math.PI * voice.phase);
    case 'triangle': {
      // Written so phase 0 is a zero crossing: a reset-phase layer then opens on silence
      // instead of on a full-scale step.
      const ramp = 4 * voice.phase;
      if (voice.phase < 0.25) return ramp;
      if (voice.phase < 0.75) return 2 - ramp;
      return ramp - 4;
    }
    case 'square': {
      let value = voice.phase < 0.5 ? 1 : -1;
      value += polyBLEP(voice.phase, increment);
      let halfCycle = voice.phase + 0.5;
      if (halfCycle >= 1) halfCycle -= 1;
      value -= polyBLEP(halfCycle, increment);
      return value;
    }
    case 'saw': {
      // Shifted half a cycle so, like the triangle, phase 0 lands on zero.
      let shifted = voice.phase + 0.5;
      if (shifted >= 1) shifted -= 1;
      return 2 * shifted - 1 - polyBLEP(shifted, increment);
    }
    case 'noise':
      return voice.random.unit() * 2 - 1;
  }
}

function biquadKindFor(type: SynthFilterType): BiquadKind | null {
  switch (type) {
    case 'off':
      return null;
    case 'lowPass':
      return 'lowPass';
    case 'highPass':
      return 'highPass';
    case 'bandPass':
      return 'bandPass';
    case 'notch':
      return 'notch';
  }
}

/** Filter coefficients are recomputed on this grid, not per sample. */
const CONTROL_BLOCK = 32;
/** Peak the finished render is normalised to, leaving a sliver of headroom. */
const NORMALISATION_PEAK_DB = -1;
/** Long enough that no speaker can click on it, short enough to never eat a tail. */
const TAIL_FADE_SECONDS = 0.003;

/**
 * Raised cosine over the last `seconds`. It arrives at zero with zero slope, so nothing
 * steps at the end of the buffer — a linear fade leaves a slope discontinuity that a hard
 * loop point turns into a tick.
 */
function applyTailFade(channels: Float32Array[], seconds: number, sampleRate: number): void {
  const requested = Math.max(2, Math.round(seconds * sampleRate));
  for (const channel of channels) {
    const fadeFrames = Math.min(channel.length, requested);
    if (fadeFrames <= 1) continue;
    const start = channel.length - fadeFrames;
    for (let offset = 0; offset < fadeFrames; offset++) {
      const position = offset / (fadeFrames - 1);
      channel[start + offset]! *= 0.5 * (1 + Math.cos(Math.PI * position));
    }
  }
}

/**
 * Always exactly two channels of equal length. An effect that changes length — time stretch,
 * pitch shift — or one that hands back a mono buffer would otherwise leave the caller with
 * something it has to special-case.
 */
function conformToStereo(channels: Float32Array[], fallbackFrameCount: number): Float32Array[] {
  const present = channels.filter((channel) => channel.length > 0);
  if (present.length === 0) {
    const silence = new Float32Array(Math.max(fallbackFrameCount, 1));
    return [silence, new Float32Array(silence.length)];
  }
  const result = present.length === 1 ? [present[0]!, present[0]!.slice()] : present.slice(0, 2);
  const frames = Math.max(result[0]!.length, result[1]!.length);
  return result.map((channel) => {
    if (channel.length === frames) return channel;
    const padded = new Float32Array(frames);
    padded.set(channel);
    return padded;
  });
}

export interface RenderPatchOptions {
  sampleRate?: number;
  budget?: RenderBudget;
}

/**
 * Renders a patch to a stereo, normalised, click-free buffer. A patch that produces no
 * sound at all returns silence rather than an empty array, so callers never special-case it.
 */
export async function renderPatch(
  requested: SynthPatch,
  options: RenderPatchOptions = {},
): Promise<RenderedAudio> {
  const patch = sanitisePatch(requested);
  const sampleRate = clamp(options.sampleRate ?? 48000, 8000, 192000);
  const frameCount = Math.max(1, Math.round(patch.duration * sampleRate));
  const budget = options.budget;

  const left = new Float32Array(frameCount);
  const right = new Float32Array(frameCount);
  const voices = makeVoices(patch);

  if (voices.length > 0) {
    const inverseSampleRate = 1 / sampleRate;
    const gateTime = gateTimeOf(patch);

    const filterKind = biquadKindFor(patch.filter.type);
    const baseCutoff = trackedCutoff(patch.filter, patch.baseFrequency);
    const maximumCutoff = sampleRate * 0.47;
    const filterLeft = new Biquad();
    const filterRight = new Biquad();

    const lfo = patch.lfo;
    const lfoRate = lfoEffectiveRate(lfo, patch.tempo);
    const lfoActive = lfoRate > 0;
    let lfoPhase = lfo.phase;
    const lfoPair = lfoSeed(patch.seed);
    const lfoRandom = new SeededRandom(lfoPair.hi, lfoPair.lo);
    let lfoHeldValue = lfoRandom.unit() * 2 - 1;

    // Saturation sits between the oscillators and the filter, exactly where an analogue
    // synth's input drive does, so the filter can tame what the drive adds.
    const driveMix = patch.drive;
    const driveGain = 1 + driveMix * 14;
    const driveNormalise = 1 / Math.tanh(driveGain);

    // Scaling the oscillator sum keeps `drive` meaning the same thing however many layers
    // a patch stacks in front of it. Final level comes from normalisation.
    let summedGain = 0;
    for (const voice of voices) summedGain += voice.gain;
    const mixNormalise = 1 / Math.max(1, summedGain);

    for (let frame = 0; frame < frameCount; frame++) {
      const time = frame * inverseSampleRate;
      const isControlFrame = frame % CONTROL_BLOCK === 0;

      // The LFO is evaluated per sample, not on the control grid: patches use it fast
      // enough to be a timbre (the zap's buzz), and stepping it there would turn that into
      // a staircase.
      let lfoValue = 0;
      if (lfoActive) {
        switch (lfo.waveform) {
          case 'sine':
            lfoValue = Math.sin(2 * Math.PI * lfoPhase);
            break;
          case 'triangle':
            lfoValue = 1 - 4 * Math.abs(lfoPhase - 0.5);
            break;
          case 'square':
            lfoValue = lfoPhase < 0.5 ? 1 : -1;
            break;
          case 'saw':
            lfoValue = 2 * lfoPhase - 1;
            break;
          case 'noise':
            lfoValue = lfoHeldValue;
            break;
        }
        lfoPhase += lfoRate * inverseSampleRate;
        if (lfoPhase >= 1) {
          lfoPhase -= Math.floor(lfoPhase);
          // A noise LFO is a sample-and-hold: one new value per cycle.
          if (lfo.waveform === 'noise') lfoHeldValue = lfoRandom.unit() * 2 - 1;
        }
      }

      let semitoneOffset = pitchEnvelopeValue(patch.pitchEnvelope, time);
      if (lfoActive && lfo.target === 'pitch') semitoneOffset += lfoValue * lfo.depth * 12;
      const pitchRatio = semitoneOffset === 0 ? 1 : Math.pow(2, semitoneOffset / 12);

      if (lfoActive && lfo.target === 'pan' && isControlFrame) {
        for (const voice of voices) {
          const gains = panGains(clamp(voice.pan + lfoValue * lfo.depth, -1, 1));
          voice.leftGain = gains.left;
          voice.rightGain = gains.right;
        }
      }

      let sumLeft = 0;
      let sumRight = 0;
      for (const voice of voices) {
        const increment = Math.min(patch.baseFrequency * voice.frequencyRatio * pitchRatio * inverseSampleRate, 0.49);
        const sample = oscillator(voice, increment);
        sumLeft += sample * voice.gain * voice.leftGain;
        sumRight += sample * voice.gain * voice.rightGain;
        voice.phase += increment;
        if (voice.phase >= 1) voice.phase -= Math.floor(voice.phase);
      }

      sumLeft *= mixNormalise;
      sumRight *= mixNormalise;

      if (driveMix > 0) {
        sumLeft = sumLeft * (1 - driveMix) + Math.tanh(sumLeft * driveGain) * driveNormalise * driveMix;
        sumRight = sumRight * (1 - driveMix) + Math.tanh(sumRight * driveGain) * driveNormalise * driveMix;
      }

      if (filterKind !== null) {
        if (isControlFrame) {
          const envelope = envelopeValue(patch.filter.envelope, time, gateTime);
          let octaves = patch.filter.envelopeAmount * envelope;
          if (lfoActive && lfo.target === 'filter') octaves += lfoValue * lfo.depth * 4;
          const cutoff = clamp(baseCutoff * Math.pow(2, octaves), 20, maximumCutoff);
          // Both channels get the same coefficients and keep their own state, so a moving
          // cutoff never resets the filter and never clicks.
          filterLeft.set(filterKind, cutoff, patch.filter.resonance, sampleRate);
          filterRight.set(filterKind, cutoff, patch.filter.resonance, sampleRate);
        }
        sumLeft = filterLeft.process(sumLeft);
        sumRight = filterRight.process(sumRight);
      }

      let amplitude = envelopeValue(patch.amplitude, time, gateTime);
      if (lfoActive && lfo.target === 'amplitude') amplitude *= 1 - lfo.depth * 0.5 * (1 - lfoValue);

      left[frame] = sumLeft * amplitude;
      right[frame] = sumRight * amplitude;

      if (budget !== undefined && frame % 16384 === 0) await budget.tick();
    }
  }

  let channels: Float32Array[] = [left, right];
  if (effectChainActiveNodes(patch.chain).length > 0) {
    // The chain works on the finished buffer, so anything with a tail — reverb, delay — is
    // only heard for as long as `duration` covers. Patches that want a tail budget for it.
    channels = await applyEffectChain(channels, patch.chain, {
      sampleRate,
      tempo: patch.tempo,
      ...(budget !== undefined ? { budget } : {}),
    });
  }

  channels = conformToStereo(channels, frameCount);
  normalizeChannels(channels, NORMALISATION_PEAK_DB);
  applyTailFade(channels, TAIL_FADE_SECONDS, sampleRate);
  return { channels, sampleRate };
}

// MARK: - Percussion templates

export type PercussionKind =
  | 'kick'
  | 'snare'
  | 'hihatClosed'
  | 'hihatOpen'
  | 'clap'
  | 'rim'
  | 'tom'
  | 'cowbell'
  | 'laser'
  | 'riser'
  | 'impact'
  | 'zap'
  | 'blip'
  | 'sweep';

/** The kit, in the order a kit is usually laid out. */
export const PERCUSSION_KIT: readonly PercussionKind[] = [
  'kick',
  'snare',
  'hihatClosed',
  'hihatOpen',
  'clap',
  'rim',
  'tom',
  'cowbell',
];

/** Transitions and one-shots rather than kit pieces. */
export const PERCUSSION_EFFECTS: readonly PercussionKind[] = [
  'laser',
  'riser',
  'impact',
  'zap',
  'blip',
  'sweep',
];

export const PERCUSSION_KINDS: readonly PercussionKind[] = [...PERCUSSION_KIT, ...PERCUSSION_EFFECTS];

export function percussionDisplayName(kind: PercussionKind): string {
  switch (kind) {
    case 'kick':
      return 'Kick';
    case 'snare':
      return 'Snare';
    case 'hihatClosed':
      return 'Closed Hat';
    case 'hihatOpen':
      return 'Open Hat';
    case 'clap':
      return 'Clap';
    case 'rim':
      return 'Rim';
    case 'tom':
      return 'Tom';
    case 'cowbell':
      return 'Cowbell';
    case 'laser':
      return 'Laser';
    case 'riser':
      return 'Riser';
    case 'impact':
      return 'Impact';
    case 'zap':
      return 'Zap';
    case 'blip':
      return 'Blip';
    case 'sweep':
      return 'Sweep';
  }
}

export function percussionSymbolName(kind: PercussionKind): string {
  switch (kind) {
    case 'kick':
      return 'circle.fill';
    case 'snare':
      return 'square.grid.2x2.fill';
    case 'hihatClosed':
      return 'aqi.low';
    case 'hihatOpen':
      return 'aqi.high';
    case 'clap':
      return 'hands.clap.fill';
    case 'rim':
      return 'seal.fill';
    case 'tom':
      return 'cylinder.fill';
    case 'cowbell':
      return 'bell.fill';
    case 'laser':
      return 'bolt.horizontal.fill';
    case 'riser':
      return 'chart.line.uptrend.xyaxis';
    case 'impact':
      return 'burst.fill';
    case 'zap':
      return 'bolt.fill';
    case 'blip':
      return 'smallcircle.filled.circle';
    case 'sweep':
      return 'wind';
  }
}

/** VoiceOver description, and the caption stored on the generated asset. */
export function percussionCaption(kind: PercussionKind): string {
  switch (kind) {
    case 'kick':
      return 'Deep sine kick with a fast pitch drop, a beater click, and grit.';
    case 'snare':
      return 'Noise and a tuned body through a band-pass. Cuts through a mix.';
    case 'hihatClosed':
      return 'Tight high-passed tick. The pulse of a beat.';
    case 'hihatOpen':
      return 'The same hat left to ring for a third of a second.';
    case 'clap':
      return 'Three noise bursts eight milliseconds apart, then a room tail.';
    case 'rim':
      return 'Short, dry, wooden click. Good for off-beats.';
    case 'tom':
      return 'Round low drum with a falling pitch and a stick edge.';
    case 'cowbell':
      return 'Two detuned squares through a band-pass. Unmistakable.';
    case 'laser':
      return 'Detuned saw falling three and a half octaves, with an echo.';
    case 'riser':
      return 'Two and a half seconds of noise opening upward. Builds a drop.';
    case 'impact':
      return 'Sub hit, noise burst, and a big reverb. Lands a transition.';
    case 'zap':
      return 'Fast electric snap with a buzzing pitch modulation.';
    case 'blip':
      return 'Tiny square blip. Menus, counters, arcade taps.';
    case 'sweep':
      return 'Filtered whoosh that travels across the stereo image.';
  }
}

/** Stable per-kind seed, so the same kit is generated every time it is asked for. */
function percussionSeed(kind: PercussionKind): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < kind.length; index++) {
    hash = Math.imul(hash ^ kind.charCodeAt(index), 0x01000193);
  }
  return hash >>> 0;
}

/**
 * The patch behind each starter sound. Every number here is a sound-design decision that
 * has been listened to; the comments explain the ones that are not obvious.
 */
export function percussionPatch(kind: PercussionKind, seed = percussionSeed(kind)): SynthPatch {
  const base = {
    name: percussionDisplayName(kind),
    symbolName: percussionSymbolName(kind),
    seed,
  };

  switch (kind) {
    case 'kick':
      // The whole sound is the pitch envelope: 50 Hz multiplied by 2^(40/12) is a 500 Hz
      // click that collapses to a sub in 40 ms. Everything else is polish.
      return makeSynthPatch({
        ...base,
        layers: [
          makeSynthLayer({ waveform: 'sine', level: 1, resetsPhase: true, startPhase: 0 }),
          makeSynthLayer({ waveform: 'noise', level: 0.45 }),
        ],
        amplitude: makeSynthEnvelope({
          attack: 0.002,
          hold: 0.01,
          decay: 0.4,
          sustain: 0,
          release: 0.03,
          curve: 0.55,
        }),
        // Parked at 220 Hz — over the sub but under the noise, so once it closes the noise
        // layer vanishes — and flung open for 30 ms by the envelope. That window is the
        // beater click, and it is the only part of a kick a phone speaker can reproduce:
        // the speaker has almost no output below 500 Hz, so without it a tapped kick is
        // silent on a phone.
        filter: makeSynthFilter({
          type: 'lowPass',
          cutoff: 220,
          resonance: 1.1,
          envelopeAmount: 4.6,
          envelope: makeSynthEnvelope({ attack: 0.0006, decay: 0.03, sustain: 0, release: 0.01, curve: 0.85 }),
        }),
        pitchEnvelope: makeSynthPitchEnvelope({ amountSemitones: 40, decay: 0.04, curve: 0.8 }),
        baseFrequency: 50,
        duration: 0.44,
        drive: 0.35,
      });

    case 'snare':
      // Two triangles a fifth apart give the drum a pitch without making it a tom; the
      // band-pass is what stops the noise sounding like a burst of static.
      return makeSynthPatch({
        ...base,
        layers: [
          makeSynthLayer({ waveform: 'triangle', level: 0.55 }),
          makeSynthLayer({ waveform: 'triangle', semitones: 7, level: 0.3 }),
          makeSynthLayer({ waveform: 'noise', level: 0.85 }),
        ],
        amplitude: makeSynthEnvelope({
          attack: 0.0006,
          hold: 0.003,
          decay: 0.16,
          sustain: 0,
          release: 0.03,
          curve: 0.6,
        }),
        filter: makeSynthFilter({
          type: 'bandPass',
          cutoff: 900,
          resonance: 0.6,
          envelopeAmount: 1.7,
          envelope: makeSynthEnvelope({ attack: 0.0006, decay: 0.06, sustain: 0.05, release: 0.03, curve: 0.8 }),
        }),
        baseFrequency: 185,
        duration: 0.19,
        drive: 0.18,
      });

    case 'hihatClosed':
      return makeSynthPatch({
        ...base,
        layers: [
          makeSynthLayer({ waveform: 'noise', level: 0.85 }),
          makeSynthLayer({ waveform: 'square', level: 0.22 }),
          makeSynthLayer({ waveform: 'square', semitones: 7, level: 0.16 }),
        ],
        amplitude: makeSynthEnvelope({
          attack: 0.0004,
          hold: 0.002,
          decay: 0.038,
          sustain: 0,
          release: 0.012,
          curve: 0.75,
        }),
        filter: makeSynthFilter({
          type: 'highPass',
          cutoff: 7000,
          resonance: 0.8,
          // Negative, so the very first milliseconds keep some low end and the hat has a
          // body instead of being pure fizz.
          envelopeAmount: -0.45,
          envelope: makeSynthEnvelope({ attack: 0.0004, decay: 0.03, sustain: 0, release: 0.01, curve: 0.8 }),
        }),
        baseFrequency: 6400,
        duration: 0.055,
        drive: 0.12,
      });

    case 'hihatOpen':
      return makeSynthPatch({
        ...base,
        layers: [
          makeSynthLayer({ waveform: 'noise', level: 0.8 }),
          makeSynthLayer({ waveform: 'square', level: 0.28 }),
          makeSynthLayer({ waveform: 'square', semitones: 7, level: 0.22 }),
        ],
        amplitude: makeSynthEnvelope({
          attack: 0.0005,
          hold: 0.004,
          decay: 0.33,
          sustain: 0.02,
          release: 0.04,
          curve: 0.7,
        }),
        filter: makeSynthFilter({
          type: 'highPass',
          cutoff: 6200,
          resonance: 0.8,
          envelopeAmount: -0.5,
          envelope: makeSynthEnvelope({ attack: 0.0005, decay: 0.12, sustain: 0, release: 0.05, curve: 0.7 }),
        }),
        baseFrequency: 6400,
        duration: 0.42,
        drive: 0.14,
      });

    case 'clap':
      // Three retriggers 8 ms apart, then a 300 ms tail. The decay has to be far shorter
      // than the gap or the bursts smear into one long noise hit.
      return makeSynthPatch({
        ...base,
        layers: [makeSynthLayer({ waveform: 'noise', level: 1 })],
        amplitude: makeSynthEnvelope({
          attack: 0.0005,
          hold: 0.0015,
          decay: 0.006,
          sustain: 0.22,
          release: 0.31,
          curve: 0.75,
          burstCount: 3,
          burstInterval: 0.008,
        }),
        filter: makeSynthFilter({
          type: 'bandPass',
          cutoff: 1000,
          resonance: 0.9,
          envelopeAmount: 0.9,
          envelope: makeSynthEnvelope({ attack: 0.0005, decay: 0.05, sustain: 0.1, release: 0.2, curve: 0.7 }),
        }),
        baseFrequency: 1000,
        duration: 0.36,
        drive: 0.15,
      });

    case 'rim':
      return makeSynthPatch({
        ...base,
        layers: [
          makeSynthLayer({ waveform: 'square', level: 0.55 }),
          makeSynthLayer({ waveform: 'square', semitones: 5, level: 0.4 }),
          makeSynthLayer({ waveform: 'noise', level: 0.25 }),
        ],
        amplitude: makeSynthEnvelope({
          attack: 0.0003,
          hold: 0.001,
          decay: 0.035,
          sustain: 0,
          release: 0.008,
          curve: 0.85,
        }),
        filter: makeSynthFilter({
          type: 'bandPass',
          cutoff: 1900,
          resonance: 3,
          envelopeAmount: 0.6,
          envelope: makeSynthEnvelope({ attack: 0.0003, decay: 0.02, sustain: 0, release: 0.008, curve: 0.85 }),
        }),
        pitchEnvelope: makeSynthPitchEnvelope({ amountSemitones: 6, decay: 0.006, curve: 0.9 }),
        baseFrequency: 1720,
        duration: 0.05,
        drive: 0.3,
      });

    case 'tom':
      return makeSynthPatch({
        ...base,
        layers: [
          makeSynthLayer({ waveform: 'sine', level: 1 }),
          makeSynthLayer({ waveform: 'triangle', level: 0.22 }),
          makeSynthLayer({ waveform: 'noise', level: 0.38 }),
        ],
        amplitude: makeSynthEnvelope({
          attack: 0.0015,
          hold: 0.006,
          decay: 0.44,
          sustain: 0,
          release: 0.05,
          curve: 0.6,
        }),
        // Opens for 50 ms to let the stick through, then sits just above the drum so the
        // noise layer stops being hiss.
        filter: makeSynthFilter({
          type: 'lowPass',
          cutoff: 380,
          resonance: 1,
          envelopeAmount: 3.6,
          envelope: makeSynthEnvelope({ attack: 0.001, decay: 0.05, sustain: 0, release: 0.03, curve: 0.85 }),
        }),
        // Gentler and longer than the kick's: a tom bends, it does not click.
        pitchEnvelope: makeSynthPitchEnvelope({ amountSemitones: 9, decay: 0.14, curve: 0.7 }),
        baseFrequency: 120,
        duration: 0.47,
        drive: 0.22,
      });

    case 'cowbell':
      // Two squares roughly a tritone-and-a-bit apart. The odd interval is the whole trick
      // — tune them to anything consonant and it stops being a cowbell.
      return makeSynthPatch({
        ...base,
        layers: [
          makeSynthLayer({ waveform: 'square', level: 0.8 }),
          makeSynthLayer({ waveform: 'square', semitones: 6.78, level: 0.7 }),
        ],
        amplitude: makeSynthEnvelope({
          attack: 0.0006,
          hold: 0.003,
          decay: 0.09,
          sustain: 0.2,
          release: 0.26,
          curve: 0.7,
        }),
        filter: makeSynthFilter({
          type: 'bandPass',
          cutoff: 1800,
          resonance: 1.2,
          envelopeAmount: 0.5,
          envelope: makeSynthEnvelope({ attack: 0.0006, decay: 0.08, sustain: 0.15, release: 0.2, curve: 0.7 }),
        }),
        baseFrequency: 540,
        duration: 0.42,
        drive: 0.25,
      });

    case 'laser':
      return makeSynthPatch({
        ...base,
        layers: [makeSynthLayer({ waveform: 'saw', level: 1, unisonVoices: 3, unisonDetuneCents: 14 })],
        amplitude: makeSynthEnvelope({
          attack: 0.002,
          hold: 0.02,
          decay: 0.42,
          sustain: 0,
          release: 0.08,
          curve: 0.4,
        }),
        filter: makeSynthFilter({
          type: 'lowPass',
          cutoff: 6000,
          resonance: 2.5,
          envelopeAmount: 1.2,
          envelope: makeSynthEnvelope({ attack: 0.002, decay: 0.4, sustain: 0.05, release: 0.1, curve: 0.6 }),
          keyTracking: 0.5,
        }),
        // Three and a half octaves of fall over half a second is what reads as a laser
        // rather than as a synth note.
        pitchEnvelope: makeSynthPitchEnvelope({ amountSemitones: 42, decay: 0.5, curve: 0.5 }),
        baseFrequency: 220,
        duration: 0.7,
        drive: 0.3,
        chain: makeEffectChain([
          makeEffectNode('delay', {
            values: { time: 0.085, feedback: 0.3, tone: 4500, sync: 0, mix: 0.22 },
          }),
        ]),
      });

    case 'riser':
      // A band-pass opening six octaves over two seconds. The slow attack on the *filter*
      // envelope is the sweep; the amplitude envelope only follows it.
      return makeSynthPatch({
        ...base,
        layers: [
          makeSynthLayer({ waveform: 'noise', level: 1 }),
          makeSynthLayer({ waveform: 'saw', level: 0.28, unisonVoices: 4, unisonDetuneCents: 30 }),
        ],
        amplitude: makeSynthEnvelope({
          attack: 1.7,
          hold: 0.05,
          decay: 0.2,
          sustain: 0.85,
          release: 0.45,
          curve: -0.5,
        }),
        filter: makeSynthFilter({
          type: 'bandPass',
          cutoff: 170,
          resonance: 4.5,
          envelopeAmount: 6,
          envelope: makeSynthEnvelope({ attack: 2, decay: 0.3, sustain: 0.9, release: 0.4, curve: -0.3 }),
        }),
        // Negative, so the pitched layer glides up into the drop rather than down.
        pitchEnvelope: makeSynthPitchEnvelope({ amountSemitones: -12, decay: 2, curve: -0.5 }),
        baseFrequency: 200,
        duration: 2.4,
        drive: 0.15,
      });

    case 'impact':
      return makeSynthPatch({
        ...base,
        layers: [
          makeSynthLayer({ waveform: 'sine', level: 1 }),
          makeSynthLayer({ waveform: 'noise', level: 0.6 }),
          makeSynthLayer({ waveform: 'triangle', semitones: 24, level: 0.24 }),
        ],
        amplitude: makeSynthEnvelope({
          attack: 0.0012,
          hold: 0.008,
          decay: 1.1,
          sustain: 0,
          release: 0.15,
          curve: 0.6,
        }),
        filter: makeSynthFilter({
          type: 'lowPass',
          cutoff: 450,
          resonance: 1,
          envelopeAmount: 4.2,
          envelope: makeSynthEnvelope({ attack: 0.001, decay: 0.16, sustain: 0, release: 0.08, curve: 0.8 }),
        }),
        pitchEnvelope: makeSynthPitchEnvelope({ amountSemitones: 24, decay: 0.09, curve: 0.8 }),
        baseFrequency: 42,
        // The dry hit is over by about 1.1 s; the rest of the duration exists so the reverb
        // tail is rendered instead of being cut off.
        duration: 2.2,
        drive: 0.45,
        chain: makeEffectChain([
          makeEffectNode('reverb', { values: { size: 0.85, damping: 0.35, preDelay: 0.02, mix: 0.42 } }),
        ]),
      });

    case 'zap':
      return makeSynthPatch({
        ...base,
        layers: [
          makeSynthLayer({ waveform: 'square', level: 0.7 }),
          makeSynthLayer({ waveform: 'saw', level: 0.6, unisonVoices: 2, unisonDetuneCents: 20 }),
        ],
        amplitude: makeSynthEnvelope({
          attack: 0.0005,
          hold: 0.004,
          decay: 0.17,
          sustain: 0,
          release: 0.025,
          curve: 0.8,
        }),
        filter: makeSynthFilter({
          type: 'lowPass',
          cutoff: 7000,
          resonance: 4,
          envelopeAmount: 1.4,
          envelope: makeSynthEnvelope({ attack: 0.0005, decay: 0.08, sustain: 0, release: 0.02, curve: 0.85 }),
          keyTracking: 0.4,
        }),
        pitchEnvelope: makeSynthPitchEnvelope({ amountSemitones: 30, decay: 0.05, curve: 0.85 }),
        // 90 Hz is far above vibrato and well inside the audible band, so the LFO stops
        // being movement and becomes part of the timbre.
        lfo: makeSynthLFO({ isEnabled: true, waveform: 'square', rateHz: 90, depth: 0.06, target: 'pitch' }),
        baseFrequency: 900,
        duration: 0.2,
        drive: 0.5,
      });

    case 'blip':
      return makeSynthPatch({
        ...base,
        layers: [
          makeSynthLayer({ waveform: 'square', level: 0.7 }),
          makeSynthLayer({ waveform: 'sine', semitones: 12, level: 0.4 }),
        ],
        amplitude: makeSynthEnvelope({
          attack: 0.001,
          hold: 0.01,
          decay: 0.085,
          sustain: 0,
          release: 0.02,
          curve: 0.5,
        }),
        filter: makeSynthFilter({
          type: 'lowPass',
          cutoff: 5200,
          resonance: 1,
          envelopeAmount: 0.8,
          envelope: makeSynthEnvelope({ attack: 0.001, decay: 0.05, sustain: 0, release: 0.02, curve: 0.7 }),
          keyTracking: 0.3,
        }),
        pitchEnvelope: makeSynthPitchEnvelope({ amountSemitones: 4, decay: 0.02, curve: 0.6 }),
        baseFrequency: 880,
        duration: 0.11,
        drive: 0.12,
      });

    case 'sweep':
      return makeSynthPatch({
        ...base,
        layers: [makeSynthLayer({ waveform: 'noise', level: 1 })],
        amplitude: makeSynthEnvelope({
          attack: 0.45,
          hold: 0.1,
          decay: 0.55,
          sustain: 0.12,
          release: 0.35,
          curve: 0,
        }),
        filter: makeSynthFilter({
          type: 'bandPass',
          cutoff: 400,
          resonance: 2,
          // Up four and a half octaves on the attack, back down on the decay — one
          // envelope, a whoosh that arrives and leaves.
          envelopeAmount: 4.5,
          envelope: makeSynthEnvelope({
            attack: 0.5,
            hold: 0.05,
            decay: 0.7,
            sustain: 0.05,
            release: 0.3,
            curve: 0,
          }),
        }),
        lfo: makeSynthLFO({ isEnabled: true, waveform: 'sine', rateHz: 0.55, depth: 0.6, target: 'pan' }),
        baseFrequency: 300,
        duration: 2,
        drive: 0.1,
      });
  }
}

// MARK: - Starter content

/**
 * PadLab generates its own starter sounds, so nothing in them belongs to anyone else. They
 * are dedicated to the public domain and can be redistributed inside a published pack.
 */
export function starterSoundLicence(): SampleLicence {
  return ccZeroLicence();
}

export interface GeneratedSound {
  kind: PercussionKind;
  name: string;
  caption: string;
  symbolName: string;
  audio: RenderedAudio;
  licence: SampleLicence;
}

export interface RenderPercussionOptions {
  sampleRate?: number;
  seed?: number;
  budget?: RenderBudget;
}

export async function renderPercussion(
  kind: PercussionKind,
  options: RenderPercussionOptions = {},
): Promise<RenderedAudio> {
  const patch = percussionPatch(kind, options.seed ?? percussionSeed(kind));
  return renderPatch(patch, {
    ...(options.sampleRate !== undefined ? { sampleRate: options.sampleRate } : {}),
    ...(options.budget !== undefined ? { budget: options.budget } : {}),
  });
}

export interface RenderKitOptions extends RenderPercussionOptions {
  kinds?: readonly PercussionKind[];
  /** 0…1 through the kit, so first-run onboarding can show real progress. */
  onProgress?: (fraction: number, kind: PercussionKind) => void;
}

/**
 * Renders the whole starter kit. Yields between sounds, so generating fourteen buffers on
 * first launch does not stall the first frame of the app.
 */
export async function renderStarterKit(options: RenderKitOptions = {}): Promise<GeneratedSound[]> {
  const kinds = options.kinds ?? PERCUSSION_KINDS;
  const budget = options.budget ?? new RenderBudget();
  const licence = starterSoundLicence();
  const sounds: GeneratedSound[] = [];

  for (let index = 0; index < kinds.length; index++) {
    const kind = kinds[index]!;
    const audio = await renderPercussion(kind, {
      ...(options.sampleRate !== undefined ? { sampleRate: options.sampleRate } : {}),
      budget,
    });
    sounds.push({
      kind,
      name: percussionDisplayName(kind),
      caption: percussionCaption(kind),
      symbolName: percussionSymbolName(kind),
      audio,
      licence,
    });
    options.onProgress?.((index + 1) / kinds.length, kind);
    await budget.tick();
  }
  return sounds;
}
